// ReviewMode.js — swipe-through note review. Swipe/drag right = accept
// (applies pending fixes for that note and resolves the card), left = skip.
// Hold the mic to dictate a correction routed to that note. Signature
// always happens in the EMR.
import React, { useRef, useState } from 'react';
import { useComprehendLive } from 'agent/useComprehendLive';
import { QUEUE_KINDS } from 'agent/liveStore';
import { T, pill, buttonGhost } from './liveTheme';

const SWIPE_THRESHOLD = 90;

const ReviewMode = () => {
  const { state, actions } = useComprehendLive();
  const [dragX, setDragX] = useState(0);
  const dragStart = useRef(null);

  const reviews = state.queue.filter(
    (q) => q.kind === QUEUE_KINDS.REVIEW && q.status === 'pending',
  );
  const current = reviews[0];
  const total = reviews.length;

  if (!current) {
    return (
      <div style={{ textAlign: 'center', padding: '24px 0' }}>
        <i
          className="fa fa-check-circle"
          aria-hidden="true"
          style={{ color: T.greenBright, fontSize: 26 }}
        />
        <p style={{ margin: '8px 0 12px', fontSize: 13, color: T.textPrimary }}>
          Review queue is clear.
        </p>
        <button
          style={buttonGhost}
          onClick={() => actions.setReviewMode(false)}
        >
          Back to Live
        </button>
      </div>
    );
  }

  const relatedNudges = state.queue.filter(
    (q) =>
      q.kind === QUEUE_KINDS.NUDGE &&
      q.conversationId === current.conversationId &&
      q.status === 'pending',
  );

  const accept = async () => {
    setDragX(0);
    // Accept = apply every pending fix on this note, then resolve the card.
    for (const nudge of relatedNudges) {
      // Sequential so note edits don't race each other.
      // eslint-disable-next-line no-await-in-loop
      await actions.fixItem(nudge);
    }
    actions.dismissItem(current);
  };

  const skip = () => {
    setDragX(0);
    actions.dismissItem(current);
  };

  const onPointerDown = (e) => {
    dragStart.current = e.clientX ?? e.touches?.[0]?.clientX;
  };
  const onPointerMove = (e) => {
    if (dragStart.current === null) return;
    const x = e.clientX ?? e.touches?.[0]?.clientX;
    setDragX(x - dragStart.current);
  };
  const onPointerUp = () => {
    if (dragX > SWIPE_THRESHOLD) accept();
    else if (dragX < -SWIPE_THRESHOLD) skip();
    else setDragX(0);
    dragStart.current = null;
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          marginBottom: 10,
        }}
      >
        <i
          className="fa fa-arrow-left"
          aria-hidden="true"
          onClick={() => actions.setReviewMode(false)}
          style={{ color: T.textSecondary, fontSize: 13, cursor: 'pointer' }}
        />
        <span style={{ color: T.textPrimary, fontSize: 12, fontWeight: 600 }}>
          Reviewing notes
        </span>
        <span style={{ marginLeft: 'auto', color: T.textFaint, fontSize: 11 }}>
          {total} left
        </span>
      </div>

      <div
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
        style={{
          background: T.surfaceCard,
          border: `1px solid ${
            dragX > SWIPE_THRESHOLD
              ? 'rgba(45,206,137,0.7)'
              : dragX < -SWIPE_THRESHOLD
                ? 'rgba(245,54,92,0.7)'
                : 'rgba(159,232,200,0.35)'
          }`,
          borderRadius: 10,
          padding: 12,
          transform: `translateX(${dragX}px) rotate(${dragX / 30}deg)`,
          transition:
            dragStart.current === null ? 'transform .15s ease' : 'none',
          touchAction: 'pan-y',
          cursor: 'grab',
          userSelect: 'none',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 8,
          }}
        >
          <div>
            <p
              style={{
                margin: 0,
                fontSize: 13,
                fontWeight: 600,
                color: T.textPrimary,
              }}
            >
              {current.patientName}
            </p>
            <p style={{ margin: 0, fontSize: 10, color: T.textSecondary }}>
              {current.detail}
            </p>
          </div>
          <span
            style={{
              marginLeft: 'auto',
              ...pill(
                current.severity === 'urgent'
                  ? 'rgba(245,54,92,0.16)'
                  : 'rgba(45,206,137,0.16)',
                current.severity === 'urgent' ? T.redSoft : T.greenBright,
              ),
            }}
          >
            {current.detail?.replace('Grade ', '') || '--'}
          </span>
        </div>

        {relatedNudges.length > 0 ? (
          <div style={{ fontSize: 11, lineHeight: 1.7 }}>
            {relatedNudges.slice(0, 3).map((n) => (
              <p key={n.id} style={{ margin: 0, color: T.amber }}>
                <i
                  className="fa fa-exclamation-circle"
                  aria-hidden="true"
                  style={{ fontSize: 10 }}
                />{' '}
                {n.title}
                {n.proposedText ? ' — fix applies on accept' : ''}
              </p>
            ))}
          </div>
        ) : (
          <p style={{ margin: 0, fontSize: 11, color: T.greenBright }}>
            <i
              className="fa fa-check"
              aria-hidden="true"
              style={{ fontSize: 10 }}
            />{' '}
            No outstanding compliance gaps
          </p>
        )}

        <p
          onClick={() => actions.openVisit(current.conversationId)}
          style={{
            margin: '8px 0 0',
            fontSize: 10,
            color: T.textFaint,
            cursor: 'pointer',
          }}
        >
          open full note ▸
        </p>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          margin: '14px 12px 4px',
        }}
      >
        <span
          onClick={skip}
          role="button"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
            color: T.redSoft,
            fontSize: 10,
            cursor: 'pointer',
          }}
        >
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 38,
              height: 38,
              borderRadius: '50%',
              border: '1px solid rgba(245,54,92,0.5)',
            }}
          >
            <i className="fa fa-times" aria-hidden="true" />
          </span>
          skip
        </span>

        <span
          onMouseDown={actions.micDown}
          onMouseUp={actions.micUp}
          onTouchStart={actions.micDown}
          onTouchEnd={actions.micUp}
          role="button"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
            color: T.textSoft,
            fontSize: 10,
            cursor: 'pointer',
          }}
        >
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 46,
              height: 46,
              borderRadius: '50%',
              background:
                state.agentStatus === 'listening'
                  ? 'rgba(45,206,137,0.35)'
                  : 'rgba(255,255,255,0.07)',
              border: '1px solid rgba(159,232,200,0.4)',
            }}
          >
            <i
              className="fa fa-microphone"
              aria-hidden="true"
              style={{ fontSize: 17, color: '#fff' }}
            />
          </span>
          hold to correct
        </span>

        <span
          onClick={accept}
          role="button"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
            color: T.greenBright,
            fontSize: 10,
            cursor: 'pointer',
          }}
        >
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 38,
              height: 38,
              borderRadius: '50%',
              background: 'rgba(45,206,137,0.18)',
              border: '1px solid rgba(45,206,137,0.6)',
            }}
          >
            <i className="fa fa-check" aria-hidden="true" />
          </span>
          accept
        </span>
      </div>
      <p
        style={{
          margin: '8px 0 0',
          textAlign: 'center',
          fontSize: 10,
          color: T.textFaint,
        }}
      >
        accept applies fixes — you still sign in your EMR
      </p>
    </div>
  );
};

export default ReviewMode;
